import {Controller} from "@hotwired/stimulus"

/**
 * Reply Controller
 * Handles reply functionality for messages and comments
 * - Shows/hides reply preview in form
 * - Manages the quoted message display
 * - Handles "See more" expansion for truncated quotes
 */
export default class extends Controller {
    static targets = [
        "replyPreview",
        "replyInput",
        "replyAuthor",
        "replyContent",
        "cancelButton",
        "expandedContent",
        "truncatedContent",
        "seeMoreButton",
        "seeLessButton"
    ]

    static values = {
        recordId: String,
        recordType: String,
        authorName: String,
        content: String,
        truncateLength: {type: Number, default: 150}
    }

    connect() {
        // Clear session storage if data-clear-reply-session attribute is present
        if (this.element.hasAttribute("data-clear-reply-session") ||
            this.element.closest("[data-clear-reply-session]")) {
            this.clearStoredReply()
        }

        // Check if there's a stored reply in sessionStorage (for page refreshes)
        this.checkStoredReply()

        // Listen for global reply events (from dropdown menus loaded via turbo frames)
        this.handleGlobalReply = this.handleGlobalReply.bind(this)
        window.addEventListener("reply:start", this.handleGlobalReply)
    }

    disconnect() {
        window.removeEventListener("reply:start", this.handleGlobalReply)
    }

    /**
     * Handle global reply event dispatched from dropdown buttons
     */
    handleGlobalReply(event) {
        const {recordId, recordType, authorName, content} = event.detail
        // Debug: ensure event is received
        console.debug("[reply] received reply:start", {recordId, recordType, authorName})

        this.recordIdValue = recordId
        this.recordTypeValue = recordType
        this.authorNameValue = authorName
        this.contentValue = content

        this.showReplyPreview()
        this.storeReply()
        this.scrollToForm()
    }

    /**
     * Start replying to a message/comment
     * Called from dropdown menu
     */
    startReply(event) {
        event.preventDefault()

        const {recordId, recordType, authorName, content} = event.currentTarget.dataset

        this.recordIdValue = recordId
        this.recordTypeValue = recordType
        this.authorNameValue = authorName
        this.contentValue = content

        this.showReplyPreview()
        this.storeReply()
        this.scrollToForm()
    }

    /**
     * Cancel the reply
     */
    cancelReply(event) {
        if (event) event.preventDefault()

        this.hideReplyPreview()
        this.clearStoredReply()
    }

    /**
     * Show reply preview in the form
     */
    showReplyPreview() {
        if (this.hasReplyPreviewTarget) {
            this.replyPreviewTarget.classList.remove("hidden")
        }

        if (this.hasReplyAuthorTarget) {
            this.replyAuthorTarget.textContent = this.authorNameValue
        }

        if (this.hasReplyContentTarget) {
            const truncated = this.truncateContent(this.contentValue)
            this.replyContentTarget.textContent = truncated
        }

        if (this.hasReplyInputTarget) {
            this.replyInputTarget.value = this.recordIdValue
        }
    }

    /**
     * Hide reply preview
     */
    hideReplyPreview() {
        if (this.hasReplyPreviewTarget) {
            this.replyPreviewTarget.classList.add("hidden")
        }

        if (this.hasReplyInputTarget) {
            this.replyInputTarget.value = ""
        }

        this.recordIdValue = ""
        this.recordTypeValue = ""
        this.authorNameValue = ""
        this.contentValue = ""
    }

    /**
     * Truncate content for preview
     */
    truncateContent(content) {
        if (!content) return ""

        // Strip HTML tags
        const stripped = content.replace(/<[^>]*>/g, "")

        if (stripped.length <= this.truncateLengthValue) {
            return stripped
        }

        return stripped.substring(0, this.truncateLengthValue) + "..."
    }

    /**
     * Store reply data in sessionStorage
     */
    storeReply() {
        const data = {
            recordId: this.recordIdValue,
            recordType: this.recordTypeValue,
            authorName: this.authorNameValue,
            content: this.contentValue
        }
        sessionStorage.setItem("pendingReply", JSON.stringify(data))
    }

    /**
     * Check for stored reply on connect
     */
    checkStoredReply() {
        const stored = sessionStorage.getItem("pendingReply")
        if (stored) {
            try {
                const data = JSON.parse(stored)
                this.recordIdValue = data.recordId
                this.recordTypeValue = data.recordType
                this.authorNameValue = data.authorName
                this.contentValue = data.content
                this.showReplyPreview()
            } catch (e) {
                this.clearStoredReply()
            }
        }
    }

    /**
     * Clear stored reply
     */
    clearStoredReply() {
        sessionStorage.removeItem("pendingReply")
    }

    /**
     * Scroll to form when starting a reply
     */
    scrollToForm() {
        const form = this.element.querySelector("form") || this.element.closest("form")
        if (form) {
            form.scrollIntoView({behavior: "smooth", block: "center"})

            // Focus the text input
            const textInput = form.querySelector('input[type="text"], textarea')
            if (textInput) {
                setTimeout(() => textInput.focus(), 300)
            }
        }
    }

    /**
     * Expand truncated quote to show full content
     */
    expand(event) {
        if (event) event.preventDefault()

        if (this.hasTruncatedContentTarget) {
            this.truncatedContentTarget.classList.add("hidden")
        }

        if (this.hasExpandedContentTarget) {
            this.expandedContentTarget.classList.remove("hidden")
        }

        if (this.hasSeeMoreButtonTarget) {
            this.seeMoreButtonTarget.classList.add("hidden")
        }

        if (this.hasSeeLessButtonTarget) {
            this.seeLessButtonTarget.classList.remove("hidden")
        }
    }

    /**
     * Collapse expanded quote back to truncated
     */
    collapse(event) {
        if (event) event.preventDefault()

        if (this.hasTruncatedContentTarget) {
            this.truncatedContentTarget.classList.remove("hidden")
        }

        if (this.hasExpandedContentTarget) {
            this.expandedContentTarget.classList.add("hidden")
        }

        if (this.hasSeeMoreButtonTarget) {
            this.seeMoreButtonTarget.classList.remove("hidden")
        }

        if (this.hasSeeLessButtonTarget) {
            this.seeLessButtonTarget.classList.add("hidden")
        }
    }

    /**
     * Called after form submission to clear reply state
     */
    formSubmitted() {
        this.clearStoredReply()
        this.hideReplyPreview()
    }
}

